import { useTranslation } from 'react-i18next';
import { useEffect, useRef } from 'react';

import {
    selectCancellationCandidate,
    selectDiscountedSubscriptions,
    selectIsReminderSet,
} from 'store/subscriptions/selectors';
import { checkBeforeFetchingDiscount, getAppropriateDiscountForSubscription } from 'store/subscriptions/helpers';
import {
    cancelSubscription,
    changePlanSuggestion,
    changeSubscription,
    fetchDiscountSubscriptions,
    pauseSubscription,
    sendFeedback,
    setCancellationCandidate,
    setIsDiscountLoaded,
    setReminder,
} from 'store/subscriptions/actions';
import { ModalName } from 'store/modals/types';

import { DISCOUNT_TYPES } from 'constants/subscriptions';
import { DATE_FORMAT } from 'constants/date';
import { reasons } from 'components/Modals/CancellationReasonModal/constants';
import { DISCOUNT_OFFER } from 'analytics/constants/ids';

import { useTrackAmplitudeEvent } from 'services/Analytics/useAmplitude';

import { useDispatch, useSelector } from 'hooks';

import { isMainSubscription } from 'helpers/subscriptions';
import { getCurrencySign, getPriceForCurrency } from 'helpers/prices';
import { getFormattedDate } from 'helpers/date';
import { isPauseFlow, checkIsPaymentFormNeeded } from './helpers';

import { CancellationFlow, IUserFeedback } from 'components/Modals/CancellationReasonModal/types';

import { IDiscountsForSubscriptions, IDiscountSubscription, ISubscription } from 'types/subscription';
import { Product, SubscriptionProductCodeTypes } from 'types/product';

import {
    cancelFlow_cancelOfferClick,
    cancelFlow_cancelOfferScreenLoad,
    cancelFlow_cancelResultClick,
    cancelFlow_cancelResultScreenLoad,
    cancelFlow_offerResultClick,
    cancelFlow_offerResultScreenLoad,
    cancelFlow_otherReasonClick,
    cancelFlow_otherReasonScreenLoad,
    OfferTypes,
} from 'analytics/trackers/cancelFlow';
import { useCommonSubscriptionLogic } from './index';
import { useModals } from '../modals';

const useCancelSubscription = (subs?: ISubscription[]) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { openModal, closeModal } = useModals();
    const {
        getDiscountSubscriptionInfo,
        displaySurveyModal,
        displayUpdatingProcessModal,
        displayProcessingIssue,
        displayCancellingIssue,
        displayReturnOffer,
        displayCancelProcessModal,
    } = useCommonSubscriptionLogic(subs);
    const cancellationCandidate = useSelector(selectCancellationCandidate);

    const cancellationCandidateRef = useRef<ISubscription | null>(null);
    const specialOfferProductRef = useRef<IDiscountsForSubscriptions>({});
    const trackAmplitudeEvent = useTrackAmplitudeEvent();

    const discountedSubscriptions: IDiscountsForSubscriptions = useSelector(selectDiscountedSubscriptions);
    const isReminderSet: boolean = useSelector(selectIsReminderSet);

    useEffect(() => {
        specialOfferProductRef.current = discountedSubscriptions;
    }, [discountedSubscriptions]);

    const onModalClick = (
        action: 'continue' | 'close' | 'try_again' | 'contact_support' | 'got_it',
        result: 'success' | 'fail',
        offer_type: OfferTypes,
        subscription_id: string
    ) => {
        cancelFlow_offerResultClick({ action, result, offer_type, subscription_id });
        closeModal();
    };

    const initSubscriptionCancellation = (candidate: ISubscription) => {
        dispatch(setCancellationCandidate(candidate));
        cancellationCandidateRef.current = candidate;

        if (candidate.is_promo_code_used) {
            onCancelSubscription();

            return;
        }

        if (candidate.fee?.fee_enabled) {
            openModal(checkIsPaymentFormNeeded(candidate) ? ModalName.feePayment : ModalName.feeAlert);

            return;
        }

        // Check if the discount is already present in the state
        const existingDiscount = getAppropriateDiscountForSubscription(
            candidate,
            discountedSubscriptions,
            DISCOUNT_TYPES.base
        );

        const shouldProceedWithFetch = checkBeforeFetchingDiscount(candidate);

        if (!existingDiscount && shouldProceedWithFetch) {
            dispatch(
                fetchDiscountSubscriptions({
                    discount_type: DISCOUNT_TYPES.base,
                    id: candidate?.external_id as string,
                    provider: candidate.payment_provider,
                })
            );
        } else {
            dispatch(setIsDiscountLoaded(true));
        }

        handleSurveyModal(onSubmitSurvey);
    };

    const initSubscriptionPause = (candidate: ISubscription, isPauseFlow?: boolean) => {
        dispatch(setCancellationCandidate(candidate));
        cancellationCandidateRef.current = candidate;
        onPauseSubscription(isPauseFlow);
    };

    const handleDiscountAppliedPopupClosed = () => {
        cancelFlow_offerResultClick({ action: 'continue', result: 'success', offer_type: OfferTypes.Discount_offer });
        closeModal();
    };

    const handleSubscriptionCanceledPopupClosed = (candidate: ISubscription, action: 'got_it' | 'close') => {
        cancelFlow_cancelResultClick({ action, result: 'success', subscription_id: candidate.external_id });
        displayReturnOffer(candidate, true);
    };

    const onSuccessfulChangeSubscription = (product: IDiscountSubscription, external_id: string) => {
        cancelFlow_offerResultScreenLoad({
            result: 'success',
            offer_type: OfferTypes.Discount_offer,
            subscription_id: external_id,
        });

        openModal(ModalName.universalModal, {
            image: 'rising-hands',
            onClose: () => handleDiscountAppliedPopupClosed(),
            onPrimaryClick: () => handleDiscountAppliedPopupClosed(),
            withCloseButton: true,
            captions: {
                title: t('subscription.specialOffer.offerApplied.title'),
                extraText: {
                    text: t('subscription.specialOffer.offerApplied.description', {
                        currencySign: getCurrencySign(product?.currency),
                        price: getPriceForCurrency(product),
                        period: cancellationCandidateRef.current?.subscription_period,
                    }),
                    color: 'text-main',
                },
                primaryButton: t('basics.continue'),
            },
            dataLocators: {
                modal: 'cancelSubModal',
                container: 'updatingSubscriptionPlanModal',
                primaryButton: 'updatingSubscriptionPlanContinueButton',
                closeButton: 'updatingSubscriptionPlanCloseButton',
            },
        });
    };

    const onSuccessfulCancelSubscription = (candidate: ISubscription) => {
        openModal(ModalName.universalModal, {
            onClose: () => handleSubscriptionCanceledPopupClosed(candidate, 'close'),
            onPrimaryClick: () => handleSubscriptionCanceledPopupClosed(candidate, 'got_it'),
            withCloseButton: true,
            captions: {
                title: t('subscription.cancel.cancelModal.title'),
                extraText: {
                    text: t('subscription.cancel.cancelModal.subtitle'),
                },
                primaryButton: t('basics.gotIt'),
            },
            dataLocators: {
                modal: 'cancelSubModal',
                container: 'cancelSubscriptionPlanModal',
                primaryButton: 'cancelSubscriptionPlanContinueButton',
                closeButton: 'cancelSubscriptionPlanCloseButton',
            },
        });
    };

    const handleSubmitSpecialOffer = () => {
        const candidate = cancellationCandidateRef.current as ISubscription;
        const specialOfferProduct = getAppropriateDiscountForSubscription(
            candidate,
            specialOfferProductRef.current as IDiscountsForSubscriptions,
            DISCOUNT_TYPES.base
        );

        if (!specialOfferProduct) return;

        const {
            id,
            name,
            amount,
            currency,
            started_from,
            trial,
            product_id,
            subscription_period,
            subscription_trial_period,
            price,
            trial_price,
        } = specialOfferProduct;

        displayUpdatingProcessModal();

        dispatch(
            changeSubscription({
                onError: () => {
                    handleChangingSubscriptionIssue(() => {
                        // TODO
                    }, handleSubmitSpecialOffer);
                },
                onSuccess: () => {
                    onSuccessfulChangeSubscription(specialOfferProduct, candidate.external_id);
                },
                subscription: {
                    id: candidate.external_id,
                    provider: candidate.payment_provider,
                    product: {
                        price,
                        trial_price,
                        id,
                        name,
                        trial,
                        amount,
                        product_id,
                        currency,
                        started_from,
                        subscription_period: subscription_period,
                        subscription_trial_period: subscription_trial_period,
                    },
                    discount_type: DISCOUNT_TYPES.base,
                },
            })
        );
    };

    const moveBack = () => {
        closeModal();

        if (cancellationCandidateRef.current) {
            initSubscriptionCancellation(cancellationCandidateRef.current);
        }
    };

    const handleChangingSubscriptionIssue = (
        trackLoad: () => void,
        retryCall: (discountedSubscriptions?: IDiscountSubscription, candidate?: ISubscription) => void
    ) => {
        displayProcessingIssue(OfferTypes.Discount_offer, trackLoad, retryCall);
    };

    const onCancelSubscription = () => {
        const candidate = cancellationCandidateRef.current ?? cancellationCandidate;

        if (!candidate) return closeModal();

        const runCancel = () => {
            dispatch(
                cancelSubscription({
                    id: candidate.external_id,
                    provider: candidate.payment_provider,
                    onError: (errMsg?: string) => {
                        displayCancellingIssue(onCancelSubscription, candidate.external_id, errMsg);

                        cancelFlow_cancelResultScreenLoad({
                            subscription_id: candidate.external_id,
                            result: 'fail',
                        });
                    },
                    onSuccess: () => {
                        onSuccessfulCancelSubscription(candidate);

                        cancelFlow_cancelResultScreenLoad({
                            subscription_id: candidate.external_id,
                            result: 'success',
                        });
                    },
                    onCancel: closeModal,
                })
            );
        };

        displayCancelProcessModal(candidate);

        const shouldProceedWithFetch = checkBeforeFetchingDiscount(candidate);

        if (shouldProceedWithFetch) {
            dispatch(
                fetchDiscountSubscriptions({
                    discount_type: DISCOUNT_TYPES.extra,
                    provider: candidate.payment_provider,
                    id: candidate?.external_id as string,
                    onError: () => {
                        runCancel();
                    },
                    onSuccess: () => {
                        runCancel();
                    },
                })
            );
        } else {
            runCancel();
        }
    };

    type OnSubmitSurveyOptions = {
        skipFeedbackSend?: boolean;
    };

    const onSubmitSurvey = ({ reason }: IUserFeedback, options: OnSubmitSurveyOptions = {}) => {
        const candidate = cancellationCandidateRef.current as ISubscription;
        const discountedSubscription: IDiscountSubscription | undefined = getAppropriateDiscountForSubscription(
            candidate,
            specialOfferProductRef.current as IDiscountsForSubscriptions,
            DISCOUNT_TYPES.base
        );

        const isCurrentSubscriptionMain = isMainSubscription(candidate.product_type);

        const theReason = reason ? reason.shortKey : '';

        if (!options.skipFeedbackSend && theReason !== 'other') {
            dispatch(
                sendFeedback({
                    reason: theReason,
                })
            );
        }

        if (reason?.flow === CancellationFlow.withPlanSuggestion) {
            onPlanSuggestionOffer();

            return;
        }

        if (reason?.flow === CancellationFlow.withFeedbackForm) {
            onFeedbackFormOffer();

            return;
        }

        if (reason?.flow === CancellationFlow.withReminderOffer && !isReminderSet && isCurrentSubscriptionMain) {
            onReminderOffer();

            return;
        }

        const shouldRunPauseFlow = isPauseFlow({
            flow: String(reason?.flow),
            productCodeType: String(candidate?.product_type) as SubscriptionProductCodeTypes,
            wasPaused: Boolean(candidate?.pause.pause_from),
        });

        if (shouldRunPauseFlow) {
            onPauseSubscription();

            return;
        }

        if (!discountedSubscription) {
            // if no appropriate product found - immediately cancelling the subscription
            onCancelSubscription();

            return;
        }

        const { isSpecialOfferLessExpensive, discount } = getDiscountSubscriptionInfo(
            candidate,
            discountedSubscription
        );

        if (isSpecialOfferLessExpensive && !candidate.discount?.discount_granted_at && !candidate.is_promo_code_used) {
            openModal(ModalName.discountOfferModal, {
                onClose: () => {
                    closeModal();

                    cancelFlow_cancelOfferClick({
                        action: 'close',
                        offer_type: OfferTypes.Discount_offer,
                        subscription_id: candidate.external_id,
                    });
                    trackAmplitudeEvent('cancel_offer__click', { offer_type: OfferTypes.Discount_offer });
                },
                onDiscard: () => {
                    onCancelSubscription();

                    cancelFlow_cancelOfferClick({
                        action: 'cancel',
                        offer_type: OfferTypes.Discount_offer,
                        subscription_id: candidate.external_id,
                    });
                    trackAmplitudeEvent('cancel_offer__click', { offer_type: OfferTypes.Discount_offer });
                },
                onSubmit: () => {
                    handleSubmitSpecialOffer();

                    cancelFlow_cancelOfferClick({
                        action: 'claim_offer',
                        offer_type: OfferTypes.Discount_offer,
                        subscription_id: candidate.external_id,
                    });
                    trackAmplitudeEvent('cancel_offer__click', { offer_type: OfferTypes.Discount_offer });
                },
                onMoveBack: () => {
                    moveBack();

                    cancelFlow_cancelOfferClick({
                        action: 'close',
                        offer_type: OfferTypes.Discount_offer,
                        subscription_id: candidate.external_id,
                    });
                    trackAmplitudeEvent('cancel_offer__click', { offer_type: OfferTypes.Discount_offer });
                },
                trackLoad: () => {
                    cancelFlow_cancelOfferScreenLoad({
                        offer_type: OfferTypes.Discount_offer,
                        subscription_id: candidate.external_id,
                    });
                    trackAmplitudeEvent('cancel_offer__screen__load');
                },
                captions: {
                    headerTitle: t('subscription.text.cancel'),
                    title: 'subscription.discountOffer.title',
                    firstBadgeText: t('subscription.cancellation.specialOffer.firstBadge'),
                    secondBadgeText: t('subscription.cancellation.specialOffer.withDynamicPercent.secondBadge', {
                        discount: discount,
                    }),
                    subTitle: t('subscription.discountOffer.subTitle'),
                    extraText: t('subscription.discountOffer.disclaimerNew'),
                    submitButton: t('subscription.discountOffer.button.changePlan'),
                    discardButton: t('subscription.text.cancel'),
                    periodTitle: t('subscription.cancellation.specialOffer.namePeriodICU', {
                        period: discountedSubscription.subscription_period,
                    }),
                    period: discountedSubscription.subscription_period,
                    bottomDisclaimer: t('subscription.discountOffer.bottomDisclaimer.text'),
                },
                oldProduct: candidate?.product as Product,
                specialOfferProduct: discountedSubscription,
                discount,
                isCurrentSubscriptionMain,
            });

            return;
        }

        console.warn('Special offer is not available');
        onCancelSubscription();
    };

    const handleSurveyModal = (onSubmitSurvey: ({ reason }: IUserFeedback) => void) => {
        displaySurveyModal(onSubmitSurvey, onCancelSubscription, DISCOUNT_OFFER);
    };

    const onPauseSubscription = (isPauseFlow?: boolean) => {
        const candidate = cancellationCandidateRef.current as ISubscription;

        const onPauseSubmit = () => {
            const candidate = cancellationCandidateRef.current as ISubscription;

            if (!candidate) return closeModal();

            displayUpdatingProcessModal();

            dispatch(
                pauseSubscription({
                    id: candidate.external_id,
                    provider: candidate.payment_provider,
                    onSuccess: (subs) => {
                        const data = subs.find((s) => s.external_id === candidate.external_id)!;
                        const pauseToDate = getFormattedDate(data.pause.pause_to!, DATE_FORMAT);

                        cancelFlow_offerResultScreenLoad({
                            result: 'success',
                            offer_type: OfferTypes.Pause_offer,
                            subscription_id: candidate.external_id,
                        });

                        openModal(ModalName.universalModal, {
                            image: 'thumbUp',
                            onClose: () =>
                                onModalClick('close', 'success', OfferTypes.Pause_offer, candidate.external_id),
                            onPrimaryClick: () =>
                                onModalClick('got_it', 'success', OfferTypes.Pause_offer, candidate.external_id),
                            withCloseButton: true,
                            captions: {
                                title: t('subscription.pause.success.title'),
                                subtitle: t('subscription.cancelFlow.pauseOffer.pause.successModal.subtitle', {
                                    pauseToDate,
                                }),
                                primaryButton: t('basics.gotIt'),
                            },
                            dataLocators: {
                                modal: 'cancelSubModal',
                                container: 'updatingSubscriptionPlanModal',
                                primaryButton: 'updatingSubscriptionPlanContinueButton',
                                closeButton: 'updatingSubscriptionPlanCloseButton',
                            },
                        });
                    },
                    onError: () => {
                        cancelFlow_offerResultScreenLoad({
                            result: 'fail',
                            offer_type: OfferTypes.Pause_offer,
                            subscription_id: candidate.external_id,
                        });

                        openModal(ModalName.universalModal, {
                            image: 'errorAttention',
                            onClose: () => onModalClick('close', 'fail', OfferTypes.Pause_offer, candidate.external_id),
                            onPrimaryClick: () => {
                                cancelFlow_offerResultClick({
                                    action: 'try_again',
                                    result: 'fail',
                                    offer_type: OfferTypes.Pause_offer,
                                    subscription_id: candidate.external_id,
                                });
                                onPauseSubmit();
                            },
                            onContactUsClick: () =>
                                cancelFlow_offerResultClick({
                                    action: 'contact_support',
                                    result: 'fail',
                                    offer_type: OfferTypes.Pause_offer,
                                    subscription_id: candidate.external_id,
                                }),
                            withCloseButton: true,
                            withContactUsLink: true,
                            captions: {
                                title: t('subscription.updating.issueModal.title'),
                                primaryButton: t('basics.tryAgain'),
                                tertiaryButton: t('basics.contactSupport'),
                            },
                            dataLocators: {
                                modal: 'errorCancelSubModal',
                                container: 'subscriptionCancelationModalWarning',
                                primaryButton: 'errorModalTryAgain',
                                closeButton: 'errorModalCloseButton',
                            },
                        });
                    },
                })
            );
        };

        openModal(ModalName.pauseSubscriptionModal, {
            isPauseFlow,
            trackLoad() {
                cancelFlow_cancelOfferScreenLoad({
                    offer_type: OfferTypes.Pause_offer,
                    subscription_id: candidate.external_id,
                });
                trackAmplitudeEvent('cancel_offer__screen__load');
            },
            onClose: () => {
                closeModal();

                cancelFlow_cancelOfferClick({
                    action: 'close',
                    offer_type: OfferTypes.Pause_offer,
                    subscription_id: candidate.external_id,
                });
                trackAmplitudeEvent('cancel_offer__click', { offer_type: OfferTypes.Pause_offer });
            },
            onDiscard: () => {
                onCancelSubscription();

                cancelFlow_cancelOfferClick({
                    action: 'cancel',
                    offer_type: OfferTypes.Pause_offer,
                    subscription_id: candidate.external_id,
                });
                trackAmplitudeEvent('cancel_offer__click', { offer_type: OfferTypes.Pause_offer });
            },
            onSubmit() {
                onPauseSubmit();

                cancelFlow_cancelOfferClick({
                    action: 'claim_offer',
                    offer_type: OfferTypes.Pause_offer,
                    subscription_id: candidate.external_id,
                });
                trackAmplitudeEvent('cancel_offer__click', { offer_type: OfferTypes.Pause_offer });
            },
            onMoveBack: () => {
                handleSurveyModal(onSubmitSurvey);

                cancelFlow_cancelOfferClick({
                    action: 'cancel',
                    offer_type: OfferTypes.Pause_offer,
                    subscription_id: candidate.external_id,
                });
                trackAmplitudeEvent('cancel_offer__click', { offer_type: OfferTypes.Pause_offer });
            },
        });
    };

    const onFeedbackFormOffer = () => {
        const candidate = cancellationCandidateRef.current as ISubscription;

        openModal(ModalName.feedbackForm, {
            trackLoad() {
                cancelFlow_otherReasonScreenLoad({
                    offer_type: OfferTypes.Discount_offer,
                    subscription_id: candidate.external_id,
                });
            },
            onContinue({ comment }) {
                cancelFlow_otherReasonClick({
                    comment,
                    action: 'continue',
                    offer_type: OfferTypes.Discount_offer,
                    subscription_id: candidate.external_id,
                });

                onSubmitSurvey(
                    { reason: reasons.find(({ flow }) => flow === CancellationFlow.withCancellingOffer)! },
                    { skipFeedbackSend: true }
                );
            },
            onClose() {
                cancelFlow_otherReasonClick({
                    action: 'close',
                    offer_type: OfferTypes.Discount_offer,
                    subscription_id: candidate.external_id,
                });

                closeModal();
            },
            onCancel({ comment }) {
                cancelFlow_otherReasonClick({
                    comment,
                    action: 'cancel',
                    offer_type: OfferTypes.Discount_offer,
                    subscription_id: candidate.external_id,
                });

                onCancelSubscription();
            },
            onMoveBack() {
                cancelFlow_otherReasonClick({
                    action: 'close',
                    offer_type: OfferTypes.Discount_offer,
                    subscription_id: candidate.external_id,
                });
                moveBack();
            },
        });
    };

    const onPlanSuggestionOffer = () => {
        const candidate = cancellationCandidateRef.current as ISubscription;

        openModal(ModalName.planSuggestion, {
            onApply(plan) {
                cancelFlow_cancelOfferClick({
                    action: 'claim_offer',
                    offer_type: OfferTypes.Change_plan,
                    subscription_id: candidate.external_id,
                });
                trackAmplitudeEvent('cancel_offer__click', { offer_type: OfferTypes.Change_plan });

                if (!plan) {
                    window.open(import.meta.env.APP_SETTINGS_ONLELINK, '_blank');
                } else {
                    displayUpdatingProcessModal();

                    dispatch(
                        changePlanSuggestion({
                            plan,
                            onSuccess: () => {
                                openModal(ModalName.universalModal, {
                                    image: 'smile',
                                    onClose: closeModal,
                                    onPrimaryClick: () => {
                                        closeModal();
                                    },
                                    withCloseButton: true,
                                    captions: {
                                        title: t('subscription.updatingPlan.success.title'),
                                        subtitle: t('subscription.updatingPlan.success.subtitle'),
                                        primaryButton: t('basics.continue'),
                                    },
                                });
                            },
                            onError: () => {
                                openModal(ModalName.universalModal, {
                                    image: 'errorAttention',
                                    onClose: () => {
                                        cancelFlow_offerResultClick({
                                            action: 'close',
                                            result: 'fail',
                                            offer_type: OfferTypes.Change_plan,
                                            subscription_id: candidate.external_id,
                                        });

                                        closeModal();
                                    },
                                    onPrimaryClick: () => {
                                        cancelFlow_offerResultClick({
                                            action: 'try_again',
                                            result: 'fail',
                                            offer_type: OfferTypes.Change_plan,
                                            subscription_id: candidate.external_id,
                                        });

                                        onPlanSuggestionOffer();
                                    },
                                    onContactUsClick: () =>
                                        cancelFlow_offerResultClick({
                                            action: 'contact_support',
                                            result: 'fail',
                                            offer_type: OfferTypes.Change_plan,
                                            subscription_id: candidate.external_id,
                                        }),
                                    withCloseButton: true,
                                    withContactUsLink: true,
                                    captions: {
                                        title: t('subscription.updating.issueModal.title'),
                                        primaryButton: t('basics.gotIt'),
                                        tertiaryButton: t('basics.contactSupport'),
                                    },
                                });
                            },
                        })
                    );
                }
            },
            onMoveBack: () => {
                moveBack();

                cancelFlow_cancelOfferClick({
                    action: 'close',
                    offer_type: OfferTypes.Change_plan,
                    subscription_id: candidate.external_id,
                });
                trackAmplitudeEvent('cancel_offer__click', { offer_type: OfferTypes.Change_plan });
            },
            onClose: () => {
                closeModal();

                cancelFlow_cancelOfferClick({
                    action: 'close',
                    offer_type: OfferTypes.Change_plan,
                    subscription_id: candidate.external_id,
                });
                trackAmplitudeEvent('cancel_offer__click', { offer_type: OfferTypes.Change_plan });
            },
            onCancel: () => {
                onCancelSubscription();

                cancelFlow_cancelOfferClick({
                    action: 'cancel',
                    offer_type: OfferTypes.Change_plan,
                    subscription_id: candidate.external_id,
                });
                trackAmplitudeEvent('cancel_offer__click', { offer_type: OfferTypes.Change_plan });
            },
            trackLoad() {
                cancelFlow_cancelOfferScreenLoad({
                    offer_type: OfferTypes.Change_plan,
                    subscription_id: candidate.external_id,
                });
                trackAmplitudeEvent('cancel_offer__screen__load');
            },
        });
    };

    const onReminderOffer = () => {
        const candidate = cancellationCandidateRef.current as ISubscription;
        const subscriptionPeriod = candidate.subscription_period;

        const onReminderSubmit = () => {
            if (!candidate) return closeModal();

            openModal(ModalName.waitingModal, {
                captions: {
                    title: t('basics.loading'),
                },
                dataLocatorContainer: 'updatingSubscriptionPlanLoader',
                styles: {
                    paddingTop: 28,
                    paddingBottom: 32,
                    textType: 'h5',
                },
            });

            dispatch(
                setReminder({
                    external_id: candidate.external_id,
                    provider: candidate.payment_provider,
                    onSuccess: () => {
                        openModal(ModalName.reminderSuccessModal, {
                            onClose: closeModal,
                            onSubmit: onReminderSuccessModalClick,
                            subscriptionPeriod,
                        });
                    },
                    onError: () => {
                        openModal(ModalName.errorModal, {
                            onClose: closeModal,
                            onSubmit: onErrorModalClick,
                        });
                    },
                })
            );
        };

        const onReminderSuccessModalClick = () => {
            closeModal();
        };

        const onErrorModalClick = () => {
            onReminderSubmit();
        };

        openModal(ModalName.reminderOfferModal, {
            onClose: () => {
                closeModal();

                cancelFlow_cancelOfferClick({
                    action: 'close',
                    offer_type: OfferTypes.Reminder_offer,
                    subscription_id: candidate.external_id,
                });
                trackAmplitudeEvent('cancel_offer__click', { offer_type: OfferTypes.Reminder_offer });
            },
            onDiscard: () => {
                onCancelSubscription();

                cancelFlow_cancelOfferClick({
                    action: 'cancel',
                    offer_type: OfferTypes.Reminder_offer,
                    subscription_id: candidate.external_id,
                });
                trackAmplitudeEvent('cancel_offer__click', { offer_type: OfferTypes.Reminder_offer });
            },
            onSubmit() {
                onReminderSubmit();

                cancelFlow_cancelOfferClick({
                    action: 'claim_offer',
                    offer_type: OfferTypes.Reminder_offer,
                    subscription_id: candidate.external_id,
                });
                trackAmplitudeEvent('cancel_offer__click', { offer_type: OfferTypes.Reminder_offer });
            },
            onMoveBack: () => {
                handleSurveyModal(onSubmitSurvey);

                cancelFlow_cancelOfferClick({
                    action: 'close',
                    offer_type: OfferTypes.Reminder_offer,
                    subscription_id: candidate.external_id,
                });
                trackAmplitudeEvent('cancel_offer__click', { offer_type: OfferTypes.Reminder_offer });
            },
            trackLoad() {
                cancelFlow_cancelOfferScreenLoad({
                    offer_type: OfferTypes.Reminder_offer,
                    subscription_id: candidate.external_id,
                });
                trackAmplitudeEvent('cancel_offer__screen__load');
            },
            subscriptionPeriod,
        });
    };

    return {
        initSubscriptionCancellation,
        onCancelSubscription,
        initSubscriptionPause,
        onPauseSubscription,
    };
};

export default useCancelSubscription;
